import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';

/**
 * ReconnectBanner Component
 *
 * Non-destructive, mid-session counterpart to `ConnectionErrorScreen`.
 *
 * When the token-refresh circuit breaker (api.ts) opens because of repeated
 * non-auth refresh failures (Bug B: an unbounded, silent refresh loop), the
 * app must surface that degraded state WITHOUT unmounting anything — a
 * full-screen takeover here would destroy an open PostCreator draft and
 * recreate the exact data-loss bug this fix exists to prevent. So instead of
 * a route guard, this is a slim, always-mounted top bar that leaves the
 * entire DOM/app tree untouched (renders `null` otherwise).
 *
 * Deliberately reads `connectionDegraded` — NOT `authConnectionError` (that
 * one is bootstrap-only and drives the full-screen `ConnectionErrorScreen`
 * instead). See `AuthContextType` for the distinction.
 */
export const ReconnectBanner = () => {
  const { t } = useTranslation('common');
  const { connectionDegraded, isAuthenticated, retryConnection } = useAuth();
  const [isRetrying, setIsRetrying] = useState(false);

  // Only relevant once the app has a mounted session to protect. Bootstrap
  // (not-yet-authenticated) connectivity issues are handled entirely by
  // `ConnectionErrorScreen` via `authConnectionError`.
  if (!connectionDegraded || !isAuthenticated) {
    return null;
  }

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await retryConnection();
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 top-0 z-[9999] flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 bg-amber-500 px-4 py-2 text-center text-sm font-medium text-white shadow-md"
    >
      <span className="flex items-center gap-2">
        <svg
          className="h-4 w-4 flex-shrink-0 animate-spin"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        {t('reconnect_banner_message', {
          defaultValue: "Connection lost — reconnecting. We couldn't save, we'll retry.",
        })}
      </span>
      <button
        type="button"
        onClick={handleRetry}
        disabled={isRetrying}
        className="rounded-md border border-white/70 bg-white/10 px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {isRetrying
          ? t('reconnect_banner_retrying', { defaultValue: 'Retrying…' })
          : t('reconnect_banner_retry', { defaultValue: 'Retry now' })}
      </button>
    </div>
  );
};

export default ReconnectBanner;
